import { clsx } from 'clsx';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import { FilterPillsWrapper } from '@/components/route/search/FilterPillsContext/FilterPillsContext';
import SearchResultsCtx from '@/components/route/search/SearchResults/SearchResults.context';
import SearchResultsContainer from '@/components/route/search/SearchResults/SearchResultsContainer';
import Button, { ButtonVariants } from '@/components/switchback/Button/Button';
import Heading from '@/components/switchback/Heading/Heading';
import { LIST, MAP_PIN } from '@/components/switchback/Icon/assets';
import Icon from '@/components/switchback/Icon/IconComponent';
import { breakpoint } from '@/constants/breakpoint';
import { ESearchFilters } from '@/constants/searchFilters';
import { useAppLayout } from '@/context/AppLayoutContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useFixedFooterVisibility } from '@/hooks/useFixedFooterVisibility';
import { useStickyToHeaderBottom } from '@/hooks/useStickyToHeaderBottom';
import useWindowSize from '@/hooks/useWindowSize';
import { setShouldMergeForm } from '@/redux/modules/localitySearch';
import { getSearchHistogramResults, getSearchResults } from '@/redux/modules/search';
import { setFormFilter } from '@/redux/modules/searchForm';
import { getHeaderSearchFilters } from '@/redux/selectors/globalHeader';
import { getDeliveryCenter, getDeliveryDetails } from '@/redux/selectors/mapboxPlace';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSelectedFilter } from '@/redux/selectors/search/pricing';
import { trackMapToggleEvent } from '@/services/analytics/maps';
import {
  OptimizelyFlags,
  useExperimentIsEnabled,
  useOptimizelyDecision,
} from '@/services/experiments';
import { trackEvent } from '@/services/track-event';
import { ERentalCategory } from '@/services/types/search/rentals/id';

const SearchMapContainer = dynamic(
  () => import('@/components/route/search/SearchMap/SearchMapContainer'),
  { ssr: false },
);

const ResultsSort = dynamic(() => import('@/components/route/search/ResultsSort/ResultsSort'), {
  ssr: false,
});

interface SearchComponentProps {
  initialParams?: Record<string, any>;
  bounds?: Record<string, string>;
  searchTitle: string;
  breadcrumbs?: React.ReactNode;
}

const SearchComponent: React.FC<SearchComponentProps> = ({
  initialParams = {},
  bounds,
  searchTitle,
  breadcrumbs,
}) => {
  const dispatch = useDispatch();
  const selectedFilter = useSelector(getSelectedFilter);
  const { isMobile, isAboveXL } = useBreakpoint();
  const filtersFromQuery = useSelector(getQueryParams);
  const filters = useSelector(getHeaderSearchFilters);
  const deliveryCenter = useSelector(getDeliveryCenter);
  const deliveryDetails = useSelector(getDeliveryDetails);
  const isFirstRender = useRef(true);
  const rentalCategoryFromQuery =
    ((filtersFromQuery && filtersFromQuery.rental_category) as ERentalCategory) ||
    ERentalCategory.RV;
  const isStay = rentalCategoryFromQuery === ERentalCategory.STAY;
  const isCampground = rentalCategoryFromQuery === ERentalCategory.CAMPGROUND;
  const newSearchFiltersEnabled = !isStay && !isCampground;

  const isFooterVisible = useFixedFooterVisibility();
  const { headerHeight } = useAppLayout();
  const singleTileGridExperiment = useExperimentIsEnabled(OptimizelyFlags.SINGLE_TILE_GRID);
  const moveFiltersFlagEnabled = useOptimizelyDecision(
    OptimizelyFlags.MOVE_PRO_AND_SUPERHOST_FILTERS,
  );
  const singleTileGridEnabled = singleTileGridExperiment && isAboveXL && !isStay && !isCampground;
  const [expandMapView, setExpandMapView] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [hasOtherSearchHappend, _] = useState(false);
  // Define map-related parameters that should trigger a new search but not filters

  const toggleExpandMapView = useCallback(() => {
    setExpandMapView(isExpanded => !isExpanded);
  }, []);
  const isDesktopMapViewExpanded = expandMapView && isAboveXL;

  // The map defaults to hidden on tablet/mobile, but will automatically open on desktop
  const windowWidth = useWindowSize()?.width || 0;
  const isDesktop = windowWidth >= breakpoint.xl;

  useEffect(() => {
    if (isDesktop || expandMapView) {
      setIsMapOpen(true);
      return;
    }

    setIsMapOpen(false);
  }, [expandMapView, isDesktop]);

  const resultsSectionDisplay = isMapOpen ? 'hidden xl:block' : 'block';
  useEffect(() => {
    // For the first search,
    //  use both initialParams and current query params
    // For subsequent searches, only use the current query params
    if (!filters.address) {
      return;
    }

    const searchParams: Record<string, any> = {
      [ESearchFilters.ADDRESS]: filters.address,
      ...(bounds || {}),
    };

    if (isFirstRender.current == false) {
      return;
    }
    // Update the previous search params
    // Treat the first render and map param changes as a new search
    if (isFirstRender.current) {
      // When search params change, include both initial params and current query params
      // with current query params taking precedence
      isFirstRender.current = false;
      searchParams[ESearchFilters.ADDRESS] = filters.address;
      Object.assign(searchParams, initialParams || {}, filtersFromQuery || {});
      // Ensure FILTER_TYPE is an array
      if (
        searchParams[ESearchFilters.FILTER_TYPE] &&
        typeof searchParams[ESearchFilters.FILTER_TYPE] === 'string'
      ) {
        searchParams[ESearchFilters.FILTER_TYPE] =
          searchParams[ESearchFilters.FILTER_TYPE].split(',');
      }
      searchParams[ESearchFilters.PRICE_MIN] = 0;

      dispatch(
        setFormFilter({
          filters: searchParams,
          filterMethod: null, // This indicates it's not from direct user input
          ignorePersistedFilters: false,
        }),
      );
      dispatch(setShouldMergeForm(true));
      dispatch(getSearchResults(searchParams, true));
    }
  }, [
    dispatch,
    bounds,
    filtersFromQuery,
    initialParams,
    hasOtherSearchHappend,
    moveFiltersFlagEnabled,
    filters,
    deliveryCenter,
    deliveryDetails,
  ]);

  useEffect(() => {
    if (
      selectedFilter === 'Price' ||
      (moveFiltersFlagEnabled && selectedFilter === 'More') ||
      (isMobile && selectedFilter === 'More')
    ) {
      dispatch(getSearchHistogramResults(initialParams));
    }
  }, [selectedFilter, isMobile, dispatch, initialParams, moveFiltersFlagEnabled]);
  const { stickyTopPositionRef } = useStickyToHeaderBottom(
    isMobile && !isStay && !isCampground ? `${headerHeight}px` : '-0.125rem',
  );
  const mobileViewButtonTestId = isMapOpen ? 'search-list-btn' : 'search-map-btn';
  const onMobileToggleMap = useCallback(() => {
    typeof window === 'object' && window.scrollTo({ top: 0, behavior: 'smooth' });
    const isEnabled = !isMapOpen;
    // the map should be expanded when user is in map view on MD/SM
    // breakpoint and resizes to LG
    setExpandMapView(isEnabled);
    setIsMapOpen(isEnabled);
    trackMapToggleEvent({ isEnabled });
    trackEvent({
      event: 'search/map',
      action: 'toggle',
      before: isMapOpen,
      after: isEnabled,
    });
  }, [isMapOpen, setIsMapOpen]);
  const mobileToggleMapButton = (
    <Button
      className="shadow-600"
      data-testid={mobileViewButtonTestId}
      variant={ButtonVariants.whiteContained}
      onClick={onMobileToggleMap}>
      <span className="text-gray-900">
        {isMapOpen ? (
          <FormattedMessage defaultMessage="List view" id="wkS+Z1" />
        ) : (
          <FormattedMessage defaultMessage="Map view" id="fKRVP6" />
        )}
      </span>
      <Icon className="text-gray-900" name={isMapOpen ? LIST : MAP_PIN} />
    </Button>
  );

  return (
    <>
      {/* Mobile map/list view button */}
      <div
        className={`fixed z-30 flex justify-center w-full select-none ${
          isFooterVisible ? 'bottom-[5rem]' : 'bottom-7'
        }`}>
        <div className="flex flex-row items-center xl:hidden gap-x-3">{mobileToggleMapButton}</div>
      </div>
      <div
        className="sticky z-40 w-full overflow-hidden bg-white md:z-30 border-y border-neutral-20"
        ref={stickyTopPositionRef}>
        <div className="flex items-center px-4 overflow-auto xxxl:mx-12 scrollbar-none">
          <div className="md:overflow-auto md:whitespace-nowrap">
            <FilterPillsWrapper />
          </div>
        </div>
      </div>
      <SearchResultsCtx>
        <div
          className={`relative flex ${
            !isDesktopMapViewExpanded && 'xxxl:mx-16 px-4 sm:px-10 xxxl:px-0'
          }`}>
          {!isDesktopMapViewExpanded && (
            <section
              className={`${resultsSectionDisplay} w-full mt-4 sm:mt-10 md:mt-0 max-w-80 ${
                isMapOpen
                  ? clsx({
                      'xl:w-8/12 xxl:w-3/4 xl:pr-10': !singleTileGridEnabled,
                      'xl:w-3/5 xxl:w-3/5 xxxl:w-3/5 xl:pr-10': singleTileGridEnabled,
                    })
                  : 'mx-auto'
              }`}>
              {
                <>
                  <div className="flex items-center justify-between pb-5 xs:hidden md:flex">
                    <div className="hidden md:block">
                      {breadcrumbs && (
                        <div className="text-sm md:pt-5">
                          <Breadcrumbs breadcrumbs={breadcrumbs} />
                        </div>
                      )}
                      {searchTitle && (
                        <Heading
                          className="mb-2 text-2xl text-gray-800 highlight"
                          data-testid="search-title"
                          level={1}>
                          {searchTitle}
                        </Heading>
                      )}
                    </div>
                  </div>
                  {searchTitle && (
                    <Heading className="mb-4 text-base text-gray-800 md:hidden highlight" level={1}>
                      {searchTitle}
                    </Heading>
                  )}
                </>
              }
              {newSearchFiltersEnabled && (
                <div className="pb-5">
                  <ResultsSort />
                </div>
              )}
              <SearchResultsContainer isMapOpen={isMapOpen} />
            </section>
          )}
          {isMapOpen && (
            <section
              style={
                {
                  '--header-height': `${headerHeight}px`,
                } as React.CSSProperties
              }
              className={
                isDesktopMapViewExpanded
                  ? 'h-screen bg-gray-100 xl:sticky xl:top-16 flex-1'
                  : `h-screen bg-gray-100 -mx-4 xl:sticky xl:top-[var(--header-height)] xl:ml-0 xl:-mr-10 xxxl:-mr-16 flex-1`
              }>
              <div className="h-full">
                <SearchMapContainer
                  isDesktopMapViewExpanded={isDesktopMapViewExpanded}
                  toggleExpandMapView={toggleExpandMapView}
                  isOnListingPage={true}
                />
              </div>
            </section>
          )}
        </div>
      </SearchResultsCtx>
    </>
  );
};

export default SearchComponent;
